import React, { useEffect, useState } from 'react';
import {
  Device,
  EnergyStorageCoolingSystem,
  useEnergyStorageCoolingSystemDetailQuery,
  useEnergyStorageCoolingSystemDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import { DeviceInformation } from '../shared-ui/device-info';
import { PanelFrame, ViewMode } from '../shared-ui/panel-frame';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';

interface EnergyStorageCoolingSystemProps {
  id: string;
}

export const EnergyStorageCoolingSystemComponent: React.FC<
  EnergyStorageCoolingSystemProps
> = ({ id }) => {
  const [device, setDevice] = useState<Partial<EnergyStorageCoolingSystem>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  const { data } = useEnergyStorageCoolingSystemDetailQuery({
    variables: { id },
  });

  useEnergyStorageCoolingSystemDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.energyStorageCoolingSystemById) {
      setDevice(
        data.energyStorageCoolingSystemById as Partial<EnergyStorageCoolingSystem>,
      );
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const info: DeviceInformation[] = [
        {
          'Device Identification': {
            'Device Name':
              device.name ?? 'Unnamed Energy Storage Cooling System',
            'Device ID': device.deviceId ?? 'Unidentified',
            'Device Type': device.typeDisplayName ?? 'Unknown',
            'Last Updated': moment(device.timestamp).fromNow(),
          },
          'System Metrics': {
            Temperature: formatDeviceMetric({
              value: device.temperature?.value ?? undefined,
              units: device.temperature?.units ?? undefined,
            }),
            'Cooling/Heating Status':
              device.coolingHeatingStatus?.value ?? 'Unknown',
          },
          'System Status': {
            'Power State': device.powerState?.value ? 'On' : 'Off',
            'Is Powered On': device.isPoweredOn ? 'Yes' : 'No',
            'Is Configured': device.isConfigured ? 'Yes' : 'No',
            'Is Controlled': device.isControlled ? 'Yes' : 'No',
            'Has Pending Updates': device.hasPendingUpdates ? 'Yes' : 'No',
          },
          'System Integration': {
            Property: device.property?.title ?? 'Not assigned',
            Unit: device.unit?.name ?? 'Not assigned',
            Panel: device.panel?.displayName ?? 'Not assigned',
            Source: device.source?.name ?? 'Not assigned',
          },
        },
      ];
      setDeviceInfo(info);
    }
  }, [device]);

  if (!device) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  return (
    <PanelFrame
      title="Energy Storage Cooling System"
      iconType="mdi:snowflake"
      device={device as Partial<Device>}
      infoEntries={deviceInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Temperature
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric({
                value: device.temperature?.value ?? undefined,
                units: device.temperature?.units ?? undefined,
              })}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Cooling/Heating Status
            </Typography>
            <Typography variant="h6" align="center">
              {device.coolingHeatingStatus?.value ?? 'Unknown'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </PanelFrame>
  );
};
